import React, { useCallback, useState } from "react";
import { Ban, CalendarIcon, Trash2, UserPlus, BellOff } from "lucide-react";
import dayjs from "dayjs";

import { EDSButton } from "../../../../../components/eds/EDSButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "../../../../../components/ui/popover";
import { Calendar } from "../../../../../components/ui/calendar";
import { EnquiryStatus } from "../../../../../types/enquiries";
import { BookingLabel } from "../../../../../types/VenueGroup";

export interface BulkActionBarProps {
  selectedCount: number;
  isInternal: boolean;
  userCanReject: boolean;
  userCanDelete: boolean;
  bookingLabels?: BookingLabel[];
  isApplying?: boolean;
  onClearNotifications: () => void;
  onReject: () => void;
  onDelete: () => void;
  onAssignTo?: () => void;
  onApplyBulkUpdates?: (updates: BulkUpdateFields) => Promise<void>;
}

export interface BulkUpdateFields {
  status?: EnquiryStatus;
  addLabel?: string;
  removeLabel?: string;
  followUp?: string;
}

const BULK_STATUS_OPTIONS: { value: EnquiryStatus; label: string }[] = [
  { value: "in_progress", label: "In Progress" },
  { value: "complete", label: "Confirmed" },
  { value: "cancelled", label: "Cancelled" }
];

export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  isInternal,
  userCanReject,
  userCanDelete,
  bookingLabels = [],
  isApplying = false,
  onClearNotifications,
  onReject,
  onDelete,
  onAssignTo,
  onApplyBulkUpdates
}) => {
  const [bulkStatus, setBulkStatus] = useState<EnquiryStatus | "">("");
  const [bulkAddLabel, setBulkAddLabel] = useState("");
  const [bulkRemoveLabel, setBulkRemoveLabel] = useState("");
  const [bulkFollowUp, setBulkFollowUp] = useState<Date | undefined>(undefined);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const hasUpdates =
    bulkStatus !== "" ||
    bulkAddLabel !== "" ||
    bulkRemoveLabel !== "" ||
    bulkFollowUp !== undefined;

  const handleApply = useCallback(async () => {
    if (!hasUpdates || !onApplyBulkUpdates) return;

    const updates: BulkUpdateFields = {};
    if (bulkStatus) updates.status = bulkStatus;
    if (bulkAddLabel) updates.addLabel = bulkAddLabel;
    if (bulkRemoveLabel) updates.removeLabel = bulkRemoveLabel;
    if (bulkFollowUp) {
      updates.followUp = dayjs(bulkFollowUp).format("YYYY-MM-DD");
    }

    try {
      await onApplyBulkUpdates(updates);
      setBulkStatus("");
      setBulkAddLabel("");
      setBulkRemoveLabel("");
      setBulkFollowUp(undefined);
    } catch {
      // Form fields preserved on failure for retry
    }
  }, [
    hasUpdates,
    bulkStatus,
    bulkAddLabel,
    bulkRemoveLabel,
    bulkFollowUp,
    onApplyBulkUpdates
  ]);

  const handleFollowUpSelect = useCallback((date: Date | undefined) => {
    setBulkFollowUp(date);
    setIsCalendarOpen(false);
  }, []);

  if (selectedCount === 0) return null;

  return (
    <div
      className="flex items-center gap-2 px-4 py-2 border-b border-border bg-muted/50 flex-wrap"
      role="toolbar"
      aria-label="Bulk actions"
    >
      <span className="text-sm font-medium text-foreground">
        With selected ({selectedCount}):
      </span>

      {isInternal && onAssignTo && (
        <EDSButton
          variant="secondary"
          size="sm"
          onClick={onAssignTo}
          aria-label="Assign selected enquiries to a user"
        >
          <UserPlus size={14} className="mr-1" />
          Assign to
        </EDSButton>
      )}

      {isInternal && (
        <Select
          value={bulkStatus}
          onValueChange={(value) => setBulkStatus(value as EnquiryStatus)}
        >
          <SelectTrigger
            className="h-8 w-[140px] text-xs"
            aria-label="Set status for selected enquiries"
          >
            <SelectValue placeholder="Set status" />
          </SelectTrigger>
          <SelectContent>
            {BULK_STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {isInternal && bookingLabels.length > 0 && (
        <>
          <Select
            value={bulkAddLabel}
            onValueChange={(value) => setBulkAddLabel(value)}
          >
            <SelectTrigger
              className="h-8 w-[140px] text-xs"
              aria-label="Add action to selected enquiries"
            >
              <SelectValue placeholder="Add action" />
            </SelectTrigger>
            <SelectContent>
              {bookingLabels.map((label) => (
                <SelectItem key={label.id} value={label.id}>
                  {label.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={bulkRemoveLabel}
            onValueChange={(value) => setBulkRemoveLabel(value)}
          >
            <SelectTrigger
              className="h-8 w-[140px] text-xs"
              aria-label="Remove action from selected enquiries"
            >
              <SelectValue placeholder="Remove action" />
            </SelectTrigger>
            <SelectContent>
              {bookingLabels.map((label) => (
                <SelectItem key={label.id} value={label.id}>
                  {label.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      )}

      {isInternal && (
        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
          <PopoverTrigger asChild>
            <EDSButton
              variant="secondary"
              size="sm"
              aria-label="Set follow-up date for selected enquiries"
            >
              <CalendarIcon size={14} className="mr-1" />
              {bulkFollowUp
                ? dayjs(bulkFollowUp).format("DD/MM/YYYY")
                : "Follow up"}
            </EDSButton>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={bulkFollowUp}
              onSelect={handleFollowUpSelect}
              disabled={(date) =>
                date < new Date(new Date().setHours(0, 0, 0, 0))
              }
            />
          </PopoverContent>
        </Popover>
      )}

      {isInternal && hasUpdates && (
        <EDSButton
          variant="default"
          size="sm"
          onClick={handleApply}
          disabled={isApplying}
          aria-label="Apply bulk updates to selected enquiries"
        >
          {isApplying ? "Applying..." : "Apply"}
        </EDSButton>
      )}

      {!isInternal && (
        <EDSButton
          variant="secondary"
          size="sm"
          onClick={onClearNotifications}
          aria-label="Clear notifications for selected enquiries"
        >
          <BellOff size={14} className="mr-1" />
          Clear notifications
        </EDSButton>
      )}

      {userCanReject && (
        <EDSButton
          variant="secondary"
          size="sm"
          onClick={onReject}
          aria-label="Reject selected enquiries"
        >
          <Ban size={14} className="mr-1" />
          Reject
        </EDSButton>
      )}

      {userCanDelete && (
        <EDSButton
          variant="destructive"
          size="sm"
          onClick={onDelete}
          aria-label="Delete selected enquiries"
        >
          <Trash2 size={14} className="mr-1" />
          Delete
        </EDSButton>
      )}
    </div>
  );
};
